import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, X, Send, Stethoscope } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

// Rule-based intent tree
const INTENTS = [
  { keywords: ["appointment", "book", "booking", "schedule", "visit"], reply: "I can help you request an appointment. Our team responds quickly during working hours.", action: { label: "Request Appointment", to: "/appointment" } },
  { keywords: ["implant", "missing tooth", "missing teeth"], reply: "Dental implants are a durable, natural-feeling way to replace missing teeth, planned with CBCT-guided precision.", action: { label: "Explore Implants", to: "/services/dental-implants" } },
  { keywords: ["aligner", "braces", "straighten", "crooked"], reply: "Clear aligners discreetly straighten teeth using digital planning. You can preview your result before starting.", action: { label: "Explore Aligners", to: "/services/dental-aligners" } },
  { keywords: ["root canal", "rct", "tooth pain", "infection"], reply: "Modern root canal treatment is comfortable and saves your natural tooth. Most cases are done in 1\u20132 visits.", action: { label: "Root Canal", to: "/services/root-canal-treatment" } },
  { keywords: ["wisdom"], reply: "We remove wisdom teeth only when they cause problems, with a gentle, comfortable approach.", action: { label: "Wisdom Tooth", to: "/services/wisdom-tooth-removal" } },
  { keywords: ["cancer", "ulcer", "patch", "lump", "opmd", "precancer", "screening"], reply: "Our dedicated Oral Cancer & Precancer Centre offers sensitive, specialist-led screening. Early detection can be life-saving.", action: { label: "Oral Cancer Centre", to: "/oral-cancer-centre" } },
  { keywords: ["international", "nri", "abroad", "overseas", "travel", "foreign"], reply: "We welcome international patients with remote consultation, transparent planning, and travel guidance.", action: { label: "International Patients", to: "/international-patients" } },
  { keywords: ["price", "cost", "fee", "charge", "expensive"], reply: "Costs depend on your individual diagnosis. We always explain options and pricing transparently before any treatment \u2014 the best next step is a consultation.", action: { label: "Request Appointment", to: "/appointment" } },
  { keywords: ["clean", "whitening", "hygiene", "polish"], reply: "Professional cleaning and whitening keep your smile healthy and bright. Recommended every six months.", action: { label: "View Services", to: "/services" } },
  { keywords: ["where", "location", "address", "directions", "map"], reply: "We are located in Greater Noida. You'll find our map and directions on the contact page.", action: { label: "Contact & Map", to: "/contact" } },
  { keywords: ["hours", "timing", "open", "time"], reply: "We're open Monday to Saturday, 10:00 AM \u2013 8:00 PM. Sundays by appointment.", action: { label: "Contact", to: "/contact" } },
  { keywords: ["doctor", "dentist", "dr", "who"], reply: "Our practice is led by Dr Piyush Caroli (Founder) and Dr Ritika Sharma (Consultant), each with 20+ years of experience.", action: { label: "Meet the Doctors", to: "/doctors" } },
];

const QUICK = ["Book an appointment", "Dental implants", "Oral cancer screening", "International patients"];

const FALLBACK = "I'm a simple assistant here to guide you. You can ask about our services, appointments, or the oral cancer centre \u2014 or leave your details and our team will reach out. This is general information, not medical advice.";

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hello! Welcome to Center Of Dental Treatment. How can I help you today?" },
  ]);
  const [input, setInput] = useState("");
  const [leadMode, setLeadMode] = useState(false);
  const [lead, setLead] = useState({ name: "", phone: "" });
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const pushBot = (text, action) => setMessages((m) => [...m, { from: "bot", text, action }]);

  const handleSend = (raw) => {
    const text = (raw ?? input).trim();
    if (!text) return;
    setMessages((m) => [...m, { from: "user", text }]);
    setInput("");
    const lower = text.toLowerCase();
    const match = INTENTS.find((i) => i.keywords.some((k) => lower.includes(k)));
    setTimeout(() => {
      if (match) {
        pushBot(match.reply, match.action);
      } else {
        pushBot(FALLBACK, { label: "Leave my details", leadMode: true });
      }
    }, 300);
  };

  const submitLead = async () => {
    if (!lead.name || !lead.phone) return;
    try {
      await api.post("/leads", { name: lead.name, phone: lead.phone, source: "chatbot", message: "Requested callback via chatbot" });
      trackEvent("chatbot_lead_capture");
      setLeadMode(false);
      setLead({ name: "", phone: "" });
      pushBot("Thank you! Our team will contact you shortly. Is there anything else I can help with?");
    } catch (e) {
      pushBot("Sorry, something went wrong. Please call us directly and we'll be glad to help.");
    }
  };

  return (
    <>
      <button
        data-testid="faq-chatbot-open-button"
        onClick={() => { setOpen((v) => !v); if (!open) trackEvent("chatbot_open"); }}
        className="fixed z-40 left-4 bottom-6 h-14 w-14 rounded-full bg-white border border-[color:var(--cdt-border)] shadow-xl flex items-center justify-center hover:shadow-2xl transition-shadow"
        aria-label="Open assistant"
      >
        {open ? <X className="h-6 w-6 text-[var(--cdt-primary)]" /> : <MessageSquare className="h-6 w-6 text-[var(--cdt-primary)]" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="fixed z-40 left-4 bottom-24 w-[92vw] sm:w-96 h-[70vh] sm:h-[520px] bg-white rounded-[var(--cdt-radius-lg)] border border-[color:var(--cdt-border)] shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="bg-[var(--cdt-primary)] text-white px-4 py-3 flex items-center gap-3">
              <span className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center"><Stethoscope className="h-5 w-5" /></span>
              <div>
                <p className="font-semibold text-sm leading-tight">CDT Assistant</p>
                <p className="text-[11px] text-white/70">Guidance, not medical advice</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[var(--cdt-surface-2)]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-6 ${m.from === "user" ? "bg-[var(--cdt-primary)] text-white" : "bg-white border border-[color:var(--cdt-border)] text-[var(--cdt-text)]"}`}>
                    <p>{m.text}</p>
                    {m.action && !m.action.leadMode && (
                      <button onClick={() => { setOpen(false); navigate(m.action.to); }} className="mt-2 text-xs font-semibold text-[var(--cdt-primary)] underline underline-offset-2">
                        {m.action.label} &rarr;
                      </button>
                    )}
                    {m.action && m.action.leadMode && (
                      <button onClick={() => setLeadMode(true)} className="mt-2 text-xs font-semibold text-[var(--cdt-primary)] underline underline-offset-2">
                        {m.action.label} &rarr;
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {leadMode ? (
              <div className="p-3 border-t border-[color:var(--cdt-border)] bg-white space-y-2">
                <input data-testid="chatbot-lead-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" className="w-full h-10 px-3 rounded-lg border border-[color:var(--cdt-border)] text-sm" />
                <input data-testid="chatbot-lead-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="Phone number" className="w-full h-10 px-3 rounded-lg border border-[color:var(--cdt-border)] text-sm" />
                <button data-testid="chatbot-lead-submit" onClick={submitLead} className="w-full h-10 rounded-lg bg-[var(--cdt-primary)] text-white text-sm font-semibold">Request Callback</button>
              </div>
            ) : (
              <div className="p-3 border-t border-[color:var(--cdt-border)] bg-white">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {QUICK.map((q) => (
                    <button key={q} onClick={() => handleSend(q)} className="text-[11px] px-2.5 py-1 rounded-full bg-[var(--cdt-surface-2)] border border-[color:var(--cdt-border)] text-[var(--cdt-text)] hover:bg-white">
                      {q}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="faq-chatbot-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    placeholder="Ask a question..."
                    className="flex-1 h-10 px-3 rounded-lg border border-[color:var(--cdt-border)] text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--cdt-ring)]"
                  />
                  <button data-testid="faq-chatbot-send-button" onClick={() => handleSend()} className="h-10 w-10 rounded-lg bg-[var(--cdt-primary)] text-white flex items-center justify-center">
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
